// Bulk BOM material replace toolbar (MUS-1). Requires exactly one
// selected source material; operator picks a target + optional qty scale
// and deactivation, then dry-runs before apply.
import { useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  replaceManufacturingRecipeLinesMaterial,
  type MfgRecipeLinesMaterialReplaceResponse,
} from "@/lib/manufacturingClient";
import { ManufacturingComboboxPicker } from "./ManufacturingComboboxPicker";
import { ManufacturingMaterialReplacePreviewModal } from "./ManufacturingMaterialReplacePreviewModal";

type Props = {
  selectedIds: ReadonlyArray<string>;
  allMaterials: ReadonlyArray<MfgMaterial>;
  busy: boolean;
  onClear: () => void;
  onApplied: (summary: {
    updated: number;
    recipes: number;
    source_deactivated: boolean;
  }) => void;
};

const clearBtnClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";

const applyBtnClass =
  "rounded-md bg-sky-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60";

export function ManufacturingBulkMaterialReplaceToolbar({
  selectedIds,
  allMaterials,
  busy,
  onClear,
  onApplied,
}: Props) {
  const [targetId, setTargetId] = useState("");
  const [qtyScale, setQtyScale] = useState("1");
  const [deactivateSource, setDeactivateSource] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MfgRecipeLinesMaterialReplaceResponse | null>(null);

  const byId = useMemo(() => new Map(allMaterials.map((m) => [m.id, m])), [allMaterials]);
  const sourceId = selectedIds.length === 1 ? selectedIds[0] : "";
  const source = sourceId ? byId.get(sourceId) : undefined;

  const targetOptions = useMemo(
    () =>
      allMaterials
        .filter((m) => m.id !== sourceId)
        .map((m) => ({
          key: m.id,
          label: `${m.name} (${m.code})`,
          data: m.id,
        })),
    [allMaterials, sourceId]
  );

  const scaleNum = Number(qtyScale);
  const canPreview =
    Boolean(sourceId) &&
    Boolean(targetId) &&
    targetId !== sourceId &&
    Number.isFinite(scaleNum) &&
    scaleNum > 0 &&
    !working &&
    !busy;

  async function runDryRun() {
    if (!canPreview || !sourceId) return;
    setWorking(true);
    setError(null);
    const r = await replaceManufacturingRecipeLinesMaterial({
      from_material_id: sourceId,
      to_material_id: targetId,
      qty_scale: scaleNum,
      dry_run: true,
      deactivate_source: deactivateSource,
    });
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setPreview(r.data);
  }

  async function confirmApply() {
    if (!sourceId || !targetId) return;
    setWorking(true);
    setError(null);
    const r = await replaceManufacturingRecipeLinesMaterial({
      from_material_id: sourceId,
      to_material_id: targetId,
      qty_scale: scaleNum,
      dry_run: false,
      deactivate_source: deactivateSource,
    });
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setPreview(null);
    setTargetId("");
    setQtyScale("1");
    setDeactivateSource(false);
    onApplied({
      updated: r.data.updated_count,
      recipes: r.data.recipes.length,
      source_deactivated: r.data.source_deactivated,
    });
  }

  if (selectedIds.length !== 1) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/60 p-2 dark:border-sky-900 dark:bg-sky-950/20">
        <span className="text-[11px] text-sky-900/80 dark:text-sky-200/80">
          Select exactly one material to replace it across all BOMs.
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/60 p-2 dark:border-sky-900 dark:bg-sky-950/20">
        <span className="text-xs font-semibold text-sky-900 dark:text-sky-200">
          Replace on BOMs: {source?.name ?? "material"}
        </span>
        <button type="button" className={clearBtnClass} onClick={onClear} disabled={working}>
          Clear
        </button>
        <div className="min-w-[200px] max-w-sm flex-1">
          <ManufacturingComboboxPicker<string>
            options={targetOptions}
            value={targetId}
            onPick={(opt) => setTargetId(opt.data)}
            placeholder="Pick replacement material…"
            disabled={working || busy}
          />
        </div>
        <label className="flex items-center gap-1 text-[11px] text-sky-900 dark:text-sky-200">
          Qty scale
          <input
            type="number"
            min={0.0001}
            step="any"
            className="w-16 rounded border border-sky-300 bg-white px-1 py-0.5 text-xs dark:border-sky-800 dark:bg-zinc-900"
            value={qtyScale}
            onChange={(e) => setQtyScale(e.target.value)}
            disabled={working || busy}
          />
        </label>
        <label className="flex items-center gap-1 text-[11px] text-sky-900 dark:text-sky-200">
          <input
            type="checkbox"
            checked={deactivateSource}
            onChange={(e) => setDeactivateSource(e.target.checked)}
            disabled={working || busy}
          />
          Deactivate source
        </label>
        <button type="button" className={applyBtnClass} disabled={!canPreview} onClick={() => void runDryRun()}>
          Preview…
        </button>
        {error ? <span className="text-[11px] text-red-700 dark:text-red-300">{error}</span> : null}
      </div>
      {preview ? (
        <ManufacturingMaterialReplacePreviewModal
          preview={preview}
          sourceName={source?.name ?? "source"}
          targetName={byId.get(targetId)?.name ?? "target"}
          qtyScale={scaleNum}
          deactivateSource={deactivateSource}
          busy={working}
          onCancel={() => setPreview(null)}
          onConfirm={() => void confirmApply()}
        />
      ) : null}
    </>
  );
}
